/**
 * LogAsCaseNoteAction - the email-to-case-note door (Kith note pipeline).
 *
 * A drafted email plus its suggested billable time becomes a pre-filled
 * case note: click the action, pick the client, land on the client
 * record's Notes tab with the composer seeded (narrative = email body,
 * hours = billing suggestion, occurred date = the draft's date). NOTHING
 * auto-posts; the CM reviews and posts, and the normal pipeline
 * (extraction, confirm cards, sufficiency gate) runs on the posted note.
 *
 * Why a client picker: email drafts carry NO client_id - form_input
 * .recipient is free text ("Client's daughter"), so the CM names the
 * client at click time, from CaseNoteClientChooser (which says where the
 * list comes from for each seat). The Email helper's "For" line names
 * the client with the same chooser before drafting; a named client skips
 * the question here.
 *
 * Handoff seam: src/lib/notePrefill.ts (sessionStorage, tab-scoped),
 * consumed and cleared by NoteComposer on mount.
 *
 * The SPICE face uses the same door in the same place (founder ruling:
 * SPICE notes follow the same path as care-management notes), labelled
 * "Log Session Note". The email becomes the session note under its
 * "SPICE email summary sent <today>" stamp (buildSpiceVisitNoteText),
 * the prefill says service 'spice' (the composer starts on a SPICE band,
 * the client's own session kind), and the record opens on its SPICE face.
 * On either face, when the page already names the client (`clientId`),
 * the door goes straight there; otherwise it asks. The session
 * type (SPICE or Friendly Visitor), the date and the hours are chosen in
 * the composer, never here.
 */
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canAuthorClinicalNotes } from '@/lib/capabilities';
import { writeNotePrefill } from '@/lib/notePrefill';
import { practiceTodayIso } from '@/lib/practiceDate';
import { writeRecordServiceSide } from '@/lib/recordServiceSide';
import { buildSpiceVisitNoteText } from '@/lib/spiceVisitNote';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { CaseNoteClientChooser } from './CaseNoteClientChooser';

export interface LogAsCaseNoteActionProps {
  /** The (possibly CM-edited) email body; becomes the note narrative. */
  body: string;
  /** Suggested billable hours from the draft's billing suggestion. */
  suggestedHours?: number;
  /** YYYY-MM-DD; the draft's date. Composer defaults to today if absent. */
  occurredAt?: string;
  /** The SPICE face's door: "Log Session Note" (see the file header). */
  spice?: boolean;
  /** The client the page already names, if any: the door goes straight
   *  to that client's record instead of asking. */
  clientId?: string | null;
}

export function LogAsCaseNoteAction({
  body,
  suggestedHours,
  occurredAt,
  spice = false,
  clientId: namedClientId = null,
}: LogAsCaseNoteActionProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // A seat that can never author a clinical note (mso_staff) gets no
  // door: a disabled affordance that could never work is noise.
  const canAuthorNotes = canAuthorClinicalNotes(whoami);

  if (!canAuthorNotes) return null;

  const close = (): void => {
    setOpen(false);
  };

  const label = spice ? 'Log Session Note' : 'Log as case note';

  const choose = (clientId: string): void => {
    if (spice) {
      writeNotePrefill(tenantSlug, clientId, {
        narrative: buildSpiceVisitNoteText({ emailBody: body, sentOnIso: practiceTodayIso() }),
        source: 'email_draft',
        service: 'spice',
      });
      // A client on both services opens on its SPICE face.
      writeRecordServiceSide(tenantSlug, clientId, 'spice');
    } else {
      writeNotePrefill(tenantSlug, clientId, {
        narrative: body,
        hours: suggestedHours,
        occurred_at: occurredAt,
        source: 'email_draft',
      });
    }
    close();
    navigate(`/t/${tenantSlug}/clients/${clientId}?tab=notes`);
  };

  return (
    <>
      <Button
        variant="secondary"
        disabled={body.trim() === ''}
        onClick={() => (namedClientId !== null ? choose(namedClientId) : setOpen(true))}
        data-testid="log-as-note-action"
      >
        {label}
      </Button>
      {open && (
        <Dialog title={label} onClose={close} testId="log-as-note-dialog">
          <div className="mt-2 space-y-3">
            <p className="text-sm text-ink-soft">
              Choose the client this belongs to. The note opens pre-filled for your review; nothing
              posts until you post it.
            </p>
            <CaseNoteClientChooser onChoose={(id) => choose(id)} />
          </div>
        </Dialog>
      )}
    </>
  );
}
